import React, {useContext, useEffect, useRef, useState} from 'react';

import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {SubscriptionRow} from 'src/modules/profile/account/tabs/account-profile/product-access-summary-section/subscription-row';

import Button, {FlatButton} from '@pb/kit-component-button';
import {ButtonGroup} from '@pb/kit-component-button-group';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Dialog, DialogFooter, DialogHeader, DialogHole, DialogMain} from '@pb/kit-component-dialog-v2';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Skeleton, SkeletonItem} from '@pb/kit-component-skeleton';
import {Body, Cell, Row, Table} from '@pb/kit-component-table';

import {useManageSeats} from './use-manage-seats';

import {emailText} from './user-details-drawer.module.styl';

import type {AccessPermissions} from './manage-seats-data';
import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';
import type {UserDetails} from 'src/openapi/admin/models/UserDetails';

type Target = {userId: number, accountId: number, email: string, onChanged: (details: UserDetails) => void};
type FormProps = Target & {permissions: AccessPermissions, onClose: () => void, onBusyChange: (busy: boolean) => void};
const PAGE_SIZE = 5;

const SeatRow = ({term, model, editable}: {
    term: ProductSubscription, model: ReturnType<typeof useManageSeats>, editable: boolean,
}) => {
    const id = term.productSubscriptionId;
    const disabled = model.busy || !!model.loadError;
    return (
        <Row>
            <Cell style={{borderRight: 0}}>
                <Group direction="v" gap="s" alignItems="stretch">
                    <SubscriptionRow subscription={term}/>
                    {model.errors[id] ? <Message color="error">{model.errors[id]}</Message> : null}
                </Group>
            </Cell>
            <Cell width={160} verticalAlign="middle" style={{borderLeft: 0}}>
                {editable ? (
                    <ButtonGroup
                        role="group"
                        aria-label={`Access to ${term.product.name}`}
                        selectedOptionId={model.draft?.seats[id] ? 'grant' : 'revoke'}
                        onChange={option => model.changeSeat(id, option.id === 'grant')}
                        options={[
                            {id: 'grant', label: 'Grant', buttonProps: {disabled: disabled || !model.canGrant(id)}},
                            {id: 'revoke', label: 'Revoke', buttonProps: {disabled: disabled || !model.canRevoke(id)}},
                        ]}
                    />
                ) : null}
            </Cell>
        </Row>
    );
};

export const ManageSeatsForm = ({
    userId, accountId, email, permissions, onClose, onChanged, onBusyChange,
}: FormProps) => {
    const model = useManageSeats({userId, accountId, permissions, onChanged});
    const {userTypes} = useUserTypes();
    const {getUserStatusByCode} = useUserStatuses();
    const {setSuccessMessage} = useContext(MessageContext);
    const [page, setPage] = useState(1);
    const currentPage = Math.min(page, Math.max(1, Math.ceil(model.terms.length / PAGE_SIZE)));
    const visibleTerms = model.terms.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const editable = permissions.grant || permissions.revoke;
    const platformDisabled = !permissions.platform || model.busy || !!model.loadError;
    const availableTypes = userTypes.filter(type => type.code !== 'T_NA' || model.statuses.includes('EMPTY'));
    const applyDisabled = model.busy || model.loading || !!model.loadError || !model.dirty || model.reason.length > 64;
    useEffect(() => onBusyChange(model.busy), [model.busy, onBusyChange]);
    const close = () => {
        if (!model.busy) {
            onClose();
        }
    };
    const apply = async () => {
        if (editable && await model.save()) {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
            onClose();
        }
    };
    return (
        <>
            <DialogHeader onClose={close}>Manage Seats</DialogHeader>
            <DialogMain>
                <DialogHole>
                    <Group direction="v" gap="xs" alignItems="stretch">
                        <span className={emailText} title={email}>{email}</span>
                        {model.errors.general ? <Message color="error">{model.errors.general}</Message> : null}
                        {model.loadError ? (
                            <Message color="error">
                                {model.loadError}
                                <FlatButton disabled={model.busy} onClick={model.retry}>Retry</FlatButton>
                            </Message>
                        ) : null}
                        <Skeleton shown={model.loading} view={<SkeletonItem height={200}/>}>
                            {model.draft ? (
                                <>
                                    <FormSection headline="Platform Access">
                                        {model.errors.platform ? (
                                            <Message color="error">{model.errors.platform}</Message>
                                        ) : null}
                                        <Group gap="s">
                                            <Item grow={1}>
                                                <Label>Type</Label>
                                                <ComboBox
                                                    aria-label="Type"
                                                    value={model.draft.typeCode}
                                                    disabled={platformDisabled}
                                                    onChange={model.changeType}>
                                                    {availableTypes.map(type => (
                                                        <ComboboxItem
                                                            key={type.code}
                                                            value={type.code}
                                                            caption={type.description}
                                                        />
                                                    ))}
                                                </ComboBox>
                                            </Item>
                                            <Item grow={1}>
                                                <Label>Status</Label>
                                                <ComboBox
                                                    aria-label="Status"
                                                    value={model.draft.statusCode}
                                                    disabled={platformDisabled || model.statuses.length < 2}
                                                    onChange={model.changeStatus}>
                                                    {model.statuses.map(code => (
                                                        <ComboboxItem
                                                            key={code}
                                                            value={code}
                                                            caption={getUserStatusByCode(code)?.description || code}
                                                        />
                                                    ))}
                                                </ComboBox>
                                            </Item>
                                        </Group>
                                    </FormSection>
                                    <FormSection headline="Product Seats">
                                        {permissions.products ? (
                                            <>
                                                {model.terms.length ? (
                                                    <Table borderedGrid={true}>
                                                        <Body>
                                                            {visibleTerms.map(term => (
                                                                <SeatRow
                                                                    key={term.productSubscriptionId}
                                                                    term={term}
                                                                    model={model}
                                                                    editable={editable}
                                                                />
                                                            ))}
                                                        </Body>
                                                    </Table>
                                                ) : <Message color="info">No active or upcoming Product Access Terms.</Message>}
                                                {model.terms.length > PAGE_SIZE ? (
                                                    <div style={{paddingTop: 16}}>
                                                        <PaginationStripe
                                                            goToPage={false}
                                                            pageSize={PAGE_SIZE}
                                                            totalCount={model.terms.length}
                                                            currentPage={currentPage}
                                                            onPaginationChange={value => setPage(value.currentPage)}
                                                            sizeSwitcherPosition="LEFT"
                                                            sizeSwitcherType="NONE"
                                                        />
                                                    </div>
                                                ) : null}
                                            </>
                                        ) : <Message color="info">You do not have permission to view product access.</Message>}
                                    </FormSection>
                                    {editable ? (
                                        <div style={{paddingTop: 16}}>
                                            <Label>Reason (optional)</Label>
                                            <Input
                                                aria-label="Reason (optional)"
                                                placeholder="Why is access changing?"
                                                value={model.reason}
                                                maxLength={64}
                                                resizable={false}
                                                disabled={model.busy}
                                                onChange={event => model.setReason(event.currentTarget.value)}
                                                adornment={`${model.reason.length}/64`}
                                            />
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </Skeleton>
                    </Group>
                </DialogHole>
            </DialogMain>
            <DialogFooter>
                <Group justify="flex-end" style={{width: '100%'}}>
                    <Button variant="Type.SECONDARY" disabled={model.busy} onClick={close}>Cancel</Button>
                    {editable ? (
                        <Button
                            variant="Type.PRIMARY"
                            busy={model.busy}
                            disabled={applyDisabled}
                            onClick={apply}>Apply Changes
                        </Button>
                    ) : null}
                </Group>
            </DialogFooter>
        </>
    );
};

export const EditAccessButton = (props: Target) => {
    const rights = useAdminAccessRight();
    const {accountId} = props;
    const editable = rights.canEditUserProducts(accountId);
    const products = editable || rights.canViewUserProducts(accountId);
    const permissions = {platform: editable && rights.canEditUser(accountId),
        products, grant: editable, revoke: editable};
    const [shown, setShown] = useState(false);
    const [busy, setBusy] = useState(false);
    const button = useRef<HTMLButtonElement>(null);
    const wasShown = useRef(false);
    const close = () => setShown(false);
    useEffect(() => {
        if (shown) {
            wasShown.current = true;
        } else if (wasShown.current) {
            // Wait for the overlay focus trap to release before restoring the drawer focus.
            const timer = window.setTimeout(() => button.current?.focus({preventScroll: true}), 300);
            return () => window.clearTimeout(timer);
        }
    }, [shown]);
    if (!products) {
        return null;
    }
    return (
        <>
            <FlatButton ref={button} onClick={() => setShown(true)}><Icon type={Icons.EDIT}/> Edit Access</FlatButton>
            <Dialog
                shown={shown}
                setShown={value => {
                    if (!value && !busy) {
                        close();
                    }
                }}
                wrapperProps={{
                    style: {width: 'min(65rem, calc(100vw - 2rem))'},
                    // This UI Kit version does not forward ARIA attributes to its floating dialog element.
                    ref: panel => {
                        panel?.closest('[role="dialog"]')?.setAttribute('aria-label', 'Manage Seats');
                    },
                }}>
                {shown ? (
                    <ManageSeatsForm {...props} permissions={permissions} onClose={close} onBusyChange={setBusy}/>
                ) : null}
            </Dialog>
        </>
    );
};
